import {Component, EventEmitter, input, Input, Output, SimpleChanges} from '@angular/core';
import {FlexModule} from "@angular/flex-layout";
import {FormControl, Validators} from "@angular/forms";
import {MatStep, MatStepLabel, MatStepper} from "@angular/material/stepper";
import {
  BookingSharedRatesAndChargesComponent
} from "@features/leasing/lease-bookings/manage-sublease-booking/booking-shared-rates-and-charges/booking-shared-rates-and-charges.component";
import {LeaseViewService} from "@features/leasing/lease-view/lease-view.service";
import {concatMap, forkJoin, map, tap} from "rxjs";
import {
  DateConfig
} from "../../../../../../../nter-ui-kit/src/lib/webkit/components/booking-calendar/booking-calendar.model";
import {RetailerLookupComponentOptions} from "../../../../shared/components/retailer-lookup/retailer-lookup.model";
import {DateUtilsService} from "../../../../shared/services/dateUtils.service";
import {LeaseSubLeasesService} from "../../lease-view/lease-sub-leases/lease-sub-leases.service";
import {LeasePricingPeriod, LeaseSummary, RentalPricingOption, SharedLeasesSpaceSize} from "../../leasing.model";
import {BookingConfig, PatternType, SharedSpaceAvailability, SpacePricingRateCategory} from "../bookings.model";
import {CalendarViewComponent} from "../calendar-view/calendar-view.component";
import {LeaseBookingsService} from "../lease-bookings.service";
import {
  BookingDatesAndSpacesComponent
} from "../manage-booking/booking-dates-and-spaces/booking-dates-and-spaces.component";
import {
  BookingReviewContractComponent
} from "../manage-booking/booking-review-contract/booking-review-contract.component";
import {BookingTenantInfoComponent} from "../manage-booking/booking-tenant-info/booking-tenant-info.component";

@Component({
  selector: 'app-manage-sublease-booking',
  standalone: true,
  imports: [
    BookingDatesAndSpacesComponent,
    BookingReviewContractComponent,
    BookingTenantInfoComponent,
    CalendarViewComponent,
    FlexModule,
    MatStep,
    MatStepLabel,
    MatStepper,
    BookingSharedRatesAndChargesComponent
  ],
  templateUrl: './manage-sublease-booking.component.html',
  styleUrl: '../manage-booking/manage-booking.component.scss'
})
export class ManageSubleaseBookingComponent {
  @Input() offering!: any;
  @Input() editType: 'new' | 'add' | 'edit' = 'new';
  @Input() retailerId?: number;
  @Input() parentLease!: LeaseSummary;
  @Input() lease?: LeaseSummary;
  @Input() pricingPeriod?: LeasePricingPeriod;

  /**
   * Options for the embedded retailer lookup component.
   * The options can be partially specified, allowing flexibility in customizing
   * the behavior and appearance of the component.
   *
   * Note: If only a single allowedRetailerTenantTypes is specified, the manage-booking-info step will automatically
   * pass the value and hide the selection form-group. If there are multiple allowedRetailerTenantTypes, the user is
   * able to select from the allowed list.
   */
  retailerLookupOptions = input<Partial<RetailerLookupComponentOptions>>();

  @Output() bookingSubmitted = new EventEmitter<boolean>();

  spaceAvailabilityMap: Map<string, Map<string, SharedSpaceAvailability>> = new Map();
  loadingSpaceData: boolean = false;
  availableSharedSpace: {
    totalSpace: number,
    availableSpace: number
  } = {totalSpace: 0, availableSpace: 0};

  subLeases: LeaseSummary[] = [];
  leaseOccupancy: SharedLeasesSpaceSize[] = [];
  overlappingLeaseSpaces: SharedLeasesSpaceSize[] = [];
  dateConfigs: Map<string, Map<string, DateConfig>> = new Map();

  bookingConfig: BookingConfig = new BookingConfig();
  offeringId!: string;

  parentPricingPeriods: LeasePricingPeriod[] = [];

  steps = [
    {
      label: 'Calendar',
      control: new FormControl<boolean | null>(null, Validators.required),
      editable: true,
      display: true,
      code: 'calendar'
    },
    {
      label: 'Term & Dates',
      control: new FormControl<boolean | null>(null, Validators.required),
      editable: true,
      display: true,
      code: 'dates_and_spaces'
    },
    {
      label: 'Rates & Charges',
      control: new FormControl<boolean | null>(null, Validators.required),
      editable: true,
      display: true,
      code: 'rates_and_charges'
    },
    {
      label: 'Tenant Information',
      control: new FormControl<boolean | null>(null, Validators.required),
      editable: true,
      display: true,
      code: 'tenant_info'
    },
    {
      label: 'Review & Save',
      control: new FormControl<boolean | null>(null, Validators.required),
      editable: true,
      display: true,
      code: 'review_and_submit',
    }
  ]

  currentStep = 0;

  constructor(private dateUtils: DateUtilsService,
              private bookingService: LeaseBookingsService,
              private subLeasesService: LeaseSubLeasesService,
              private leaseViewService: LeaseViewService) {
  }

  ngOnInit() {
    this.offeringId = this.offering!.offeringUuid!;

    if (this.parentLease) {
      this.initLeaseAndSpaceData();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['parentLease'] && this.parentLease) {
      this.initLeaseAndSpaceData();
    }
  }

  initLeaseAndSpaceData() {
    this.loadingSpaceData = true;

    let focusDate = new Date();
    if (this.editType == 'edit' && this.lease) focusDate = new Date(this.lease!.leaseStartDate);
    this.loadSpaceAvailabilityMap(focusDate)
      .subscribe({
        next: () => {
          this.loadSubLeases();
          this.loadingSpaceData = false;
          if ((this.editType == 'edit' || this.editType == 'add') && this.lease) {
            this.retailerId = this.lease.retailerId;
            this.bookingConfig = {...this.generateBookingFromLease()};
            this.steps = this.steps.filter(f => f.code != 'tenant_info');
            this.updateAvailableSpace(this.bookingConfig.selectedDates);
          } else if (!!this.retailerId) {
            this.bookingConfig.retailerId = this.retailerId;
            this.steps = this.steps.filter(f => f.code != 'tenant_info');
          }
        },
        error: (err) => {
          console.log(err);
          this.loadingSpaceData = false;
        }
      })
    this.loadParentPricingPeriods();
  }

  loadSpaceAvailabilityMap(focusDate: Date) {
    const fromDate = this.dateUtils.getFirstDayOfQuarter(focusDate);
    const toDate = this.dateUtils.getLastDayOfQuarter(focusDate);

    return this.bookingService.getSharedSpaceAvailabilityMap(this.parentLease.uuid, fromDate, toDate).pipe(
      tap(value => {
        this.addToSpaceMap(value);
      }),
      concatMap(() => this.loadLeaseOccupancy(fromDate, toDate)),
      map(() => void 0)
    );
  }

  loadSubLeases() {
    const page = {
      size: '1000',
      page: '0',
      sort: 'leaseStartDate,desc'
    };
    // @ts-ignore
    this.subLeasesService.getAll('', page, null).subscribe({
      next: (data: { content: LeaseSummary[] }) => {
        this.subLeases = data.content;
      },
      error: (err) => {
        console.error('Error loading subleases:', err);
      }
    })
  }

  loadLeaseOccupancy(startDate: string, endDate: string) {
    return this.bookingService.getSharedLeaseOccupancy(startDate, endDate, this.parentLease.uuid).pipe(
      tap(value => {
        this.addToOccupancyList(value);
        this.loadingSpaceData = false;
      }),
      map(() => void 0)
    );
  }

  public loadMoreSpaceData(focusDate: Date) {
    const dateMonthKey = this.dateUtils.displayMonthAndYear(focusDate)!;
    if (!this.spaceAvailabilityMap.has(dateMonthKey)) {
      this.loadingSpaceData = true;
      this.loadSpaceAvailabilityMap(focusDate)
        .subscribe(() => this.loadingSpaceData = false);
    }
  }

  public loadMoreSpaceDataForDateRange(dateRange: { start: string, end: string }, dateSelection: string[]) {
    const fromDate = this.dateUtils.getFirstDayOfQuarter(new Date(dateRange.start));
    const toDate = this.dateUtils.getLastDayOfQuarter(new Date(dateRange.end));

    const requests = [];
    let focusDate = new Date(fromDate);

    while (focusDate <= new Date(toDate)) {
      const dateMonthKey = this.dateUtils.displayMonthAndYear(focusDate)!;
      if (!this.spaceAvailabilityMap.has(dateMonthKey)) {
        requests.push(this.loadSpaceAvailabilityMap(new Date(focusDate)));
      }
      focusDate = this.dateUtils.add(focusDate, 1, "quarters")!;
    }

    if (requests.length > 0) {
      this.loadingSpaceData = true;
      forkJoin(requests).subscribe({
        next: () => {
          this.updateAvailableSpace(dateSelection);
          this.updateOverlappingLeaseSpaces(dateSelection);
          this.loadingSpaceData = false;
        },
        error: (err) => {
          console.log(err);
          this.loadingSpaceData = false;
        }
      });
    } else {
      this.updateAvailableSpace(dateSelection);
      this.updateOverlappingLeaseSpaces(dateSelection);
    }
  }

  loadParentPricingPeriods() {
    this.leaseViewService.getAllLeasePricingPeriods(this.parentLease.uuid).subscribe({
      next: (pricingPeriods) => {
        this.parentPricingPeriods = pricingPeriods;
        this.setBookingPricing();
      },
      error: (err) => {
        console.log(err);
      }
    })
  }

  setBookingPricing() {
    this.parentPricingPeriods.forEach(ea => {
      if (!ea.bookingPricing) {
        let cat = SpacePricingRateCategory.PER_PERIOD;
        if (ea.rentalPricingOption == RentalPricingOption.FIXED_PER_SQM_PER_MONTH
          || ea.rentalPricingOption == RentalPricingOption.PRICING_MODEL) cat = SpacePricingRateCategory.PER_SQM;

        ea.bookingPricing = {
          spacePricingRateCategory: cat,
          patternType: PatternType.MONTHLY,
          includeDays: [],
          excludeDays: [],
          customRates: false,
          bookingRates: [],
          bookingEstimation: null,
          applyTransactionFees: ea.applyTransactionFee ?? false,
          initialBillablePeriods: []
        };
      }
    })
  }

  generateBookingFromLease(): BookingConfig {
    let booking = new BookingConfig();
    if (this.lease) {
      if (this.editType == 'add') {
        booking.retailerId = this.lease.retailerId;
        booking.retailerCompanyName = this.lease.retailerCompanyName!;
        return booking;
      }

      if (this.lease.baseBookingConfig && this.lease.baseBookingConfig.selectedDates) {
        const selectedDates = this.lease.baseBookingConfig.selectedDates.map(m => this.dateUtils.displayShortDate(m)!);
        booking = {...this.lease.baseBookingConfig};
        booking.retailerId = this.lease.retailerId;
        booking.retailerCompanyName = this.lease.retailerCompanyName!;
        booking.preSelectedDates = selectedDates;
        booking.selectedDates = selectedDates;
      } else {

        const selectedDates = this.bookingService.generateDateRange(this.lease.leaseStartDate, this.lease.leaseEndDate);

        booking.retailerId = this.lease.retailerId;
        booking.retailerCompanyName = this.lease.retailerCompanyName!;
        booking.startDate = this.lease.leaseStartDate;
        booking.subLeaseSize = this.lease.totalSize;
        booking.endDate = this.lease.leaseEndDate;
        booking.selectedDates = selectedDates;
        booking.preSelectedDates = selectedDates;
        booking.spacePricingRateCategory = this.getCategoryFromRentalType(this.lease.rentalType);
        booking.patternType = this.getPatternTypeFromBookingConfig();
      }
    }
    return booking;
  }

  getCategoryFromRentalType(rentalType: RentalPricingOption | null | undefined): SpacePricingRateCategory {
    const perSqm = [RentalPricingOption.FIXED_PER_SQM_PER_MONTH, RentalPricingOption.PRICING_MODEL, RentalPricingOption.TURNOVER_PERCENTAGE];
    const perPeriod = [RentalPricingOption.FIXED_PER_MONTH, RentalPricingOption.FIXED_FOR_PERIOD];

    if (rentalType) {
      if (perSqm.includes(rentalType)) return SpacePricingRateCategory.PER_SQM;
      if (perPeriod.includes(rentalType)) return SpacePricingRateCategory.PER_PERIOD;
    }
    return SpacePricingRateCategory.PER_PERIOD;
  }

  getPatternTypeFromBookingConfig(): PatternType {
    if (!this.lease || !this.lease!.baseBookingConfig) return PatternType.MONTHLY;
    return this.lease!.baseBookingConfig.patternType ?? PatternType.MONTHLY;
  }

  updateAvailableSpace(dateSelection: string[]) {
    this.availableSharedSpace = {
      ...this.bookingService.getAvailableSharedSpaceForDateSelection(
        this.spaceAvailabilityMap,
        dateSelection)
    };
  }

  updateOverlappingLeaseSpaces(dateSelection: string[]) {
    this.overlappingLeaseSpaces = this.bookingService.getOccupiedSubLeasesForDateSelection(
      this.spaceAvailabilityMap,
      this.leaseOccupancy,
      dateSelection);
  }

  addToSpaceMap(newMap: Map<string, Map<string, SharedSpaceAvailability>>) {
    const currentMap = this.spaceAvailabilityMap;
    newMap.forEach((value, key) => {
      if (!currentMap.has(key)) {
        currentMap.set(key, value);
      }
    })
    this.spaceAvailabilityMap = currentMap;
    this.generateDateConfigs();
  }

  addToOccupancyList(newSpaces: SharedLeasesSpaceSize[]) {
    const existingIds = this.leaseOccupancy.map(m => m.uuid);

    const spacesToAdd = newSpaces
      .filter(f => !existingIds.includes(f.uuid));

    this.leaseOccupancy.push(...spacesToAdd);
  }

  // DATE CONFIGS

  public generateDateConfigs() {
    this.dateConfigs.clear();

    this.spaceAvailabilityMap.forEach((spaceMap, month) => {
      const monthMap = this.dateConfigs.get(month) || new Map();
      spaceMap.forEach((space, date) => {
        let label = '';
        let unavailable = false;

        if (space.availableSpace === 0) {
          label = '';
          unavailable = true;
        } else {
          label = `${space.availableSpace} m²`;
          unavailable = false;
        }

        monthMap.set(date, {
          label,
          unavailable
        });
      })
      this.dateConfigs.set(month, monthMap);
    });
  }

  // STEPPING

  isCurrentStep(code: string): boolean {
    const matchingStep = this.steps.find(f => f.code == code);
    if (matchingStep) {
      return matchingStep.code == this.steps[this.currentStep].code;
    }
    return false;
  }

  previousStep() {
    this.currentStep--;
  }

  nextStep() {
    this.steps[this.currentStep].control.setValue(true);

    this.currentStep++
    this.steps[this.currentStep].editable = true;
  }

  submitBooking(submitted: boolean = true) {
    this.steps.forEach(step => step.editable = false);
    this.bookingSubmitted.emit(submitted)
  }
}
